import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "./components/AuthLayout";
import OtpInput from "./components/OtpInput";
import {
  getAuthFlow,
  setAuthFlow,
  clearAuthFlow,
  setAuthSession,
} from "./components/authStore";
import { AuthService } from "../../services/auth.service";
import { getDashboardAccess } from "./components/authStore";
import AuthPopup from "./components/AuthPopup";

function routeFromNextAction(nextAction) {
  switch (String(nextAction || "").toUpperCase()) {
    case "CHANGE_PASSWORD":
    case "CHANGE_PASSWORD_REQUIRED":
      return "/admin/change-password";

    case "ONBOARDING":
    case "ONBOARDING_REQUIRED":
      return "/admin/onboarding";

    case "WAITING_APPROVAL":
    case "WAITING_FOR_ADMIN_APPROVAL":
      return "/admin/waiting-approval";

    case "ONBOARDING_REJECTED":
    case "REJECTED":
      return "/admin/onboarding";

    default:
      return "/admin/dashboard";
  }
}

export default function OtpVerify() {
  const nav = useNavigate();
  const flow = getAuthFlow();
  const { state } = useLocation();
  const loginPassword = state?.loginPassword || "";

  const [otp, setOtp] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const timerRef = useRef(null);

  const startTimer = () => {
    setSecondsLeft(30);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onClose: null,
  });

  const showPopup = ({ type = "error", title = "Alert", message, onClose = null }) => {
    setPopup({ open: true, type, title, message, onClose });
  };

  const closePopup = () => {
    const cb = popup.onClose;
    setPopup((p) => ({ ...p, open: false, onClose: null }));
    cb?.();
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const buttonBase =
    "w-full h-[46px] rounded-[10px] bg-[#d86a78] text-white font-semibold hover:bg-[#c95b69] active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed";

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      if (submitting) return;
      if (otp.length < 6) {
        return showPopup({
          type: "error",
          title: "Enter OTP",
          message: "Please enter 6 digit OTP.",
        });
      }

      setSubmitting(true);

      if (flow?.mode === "login") {
        const res = await AuthService.verifyOtp({
          email: flow.resetEmail,
          otp,
        });

        setAuthSession({
          accessToken: res?.accessToken,
          admin: res?.admin,
          dashboardAccess: res?.dashboardAccess,
          warehouseAccess: res?.warehouseAccess,
          currentDashboard: res?.currentDashboard,
          selectedWarehouseId: res?.selectedWarehouseId,
        });

        clearAuthFlow();
        const access = getDashboardAccess();

        if (
          access?.canOpenAdminDashboard &&
          access?.canOpenWarehouseDashboard
        ) {
          nav("/admin/select-dashboard", { replace: true });
        } else {
          nav(routeFromNextAction(res?.nextAction), { replace: true });
        }
        return;
      }

      if (!flow?.resetEmail) {
        showPopup({
          type: "error",
          title: "Email missing",
          message: "Email missing. Please restart forgot password.",
        });

        clearAuthFlow();
        nav("/admin/forgot-password");
        return;
      }

      await AuthService.forgotCheckOtp({
        email: flow.resetEmail,
        otp,
      });

      setAuthFlow({ otpVerified: true, otp });
      nav("/admin/reset-password", { replace: true });
    } catch (err) {
      showPopup({
        type: "error",
        title: "OTP verification Failed",
        message: err?.message || "OTP verification Failed",
      });

    } finally {
      setSubmitting(false);
    }
  };

  const changeEmail = () => {
    clearAuthFlow();
    nav(flow?.mode === "login" ? "/admin/login" : "/admin/forgot-password");
  };

  const resendOtp = async () => {
    try {
      if (!flow?.resetEmail) return
      showPopup({
        type: "error",
        title: "Email Missing",
        message: "Email missing. Please go back.",
      });


      setResending(true);

      if (flow?.mode === "login") {
        if (!loginPassword) {
          showPopup({
            type: "error",
            title: "Session expired",
            message: "Session expired. Please login again.",
          });

          clearAuthFlow();
          nav("/admin/login");
          return;
        }

        await AuthService.loginSendOtp({
          email: flow.resetEmail,
          password: loginPassword,
        });
      } else {
        await AuthService.forgotSendOtp({ email: flow.resetEmail });
      }

      setOtp("");
      startTimer();
    } catch (err) {
      showPopup({
        type: "error",
        title: "OTP Resend Failed",
        message: err?.message || "Failed to resend OTP",
      });

    } finally {
      setResending(false);
    }
  };

  const canResend = secondsLeft === 0 && !resending;

  return (
    <AuthLayout>
      <h1 className="mb-4 text-center text-[32px] font-bold tracking-wide text-[#d86a78]">
        ADMIN LOGIN
      </h1>

      <div className="mx-auto w-full max-w-[360px]">
        <p className="mb-6 text-center text-[13px] leading-5 text-slate-500">
          Please enter the one time password (otp) sent to your email
        </p>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="flex justify-center">
            <OtpInput length={6} value={otp} onChange={setOtp} />
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={changeEmail}
              className="text-[11px] font-medium text-slate-400 hover:text-[#d86a78]"
            >
              Change Email?
            </button>

            {secondsLeft > 0 ? (
              <div className="text-[11px] font-medium text-slate-400">
                Resend in <span className="text-slate-600">{secondsLeft}s</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={resendOtp}
                disabled={!canResend}
                className="text-[11px] font-semibold text-[#d86a78] hover:text-[#c95b69] disabled:opacity-60"
              >
                {resending ? "Resending..." : "Resend OTP"}
              </button>
            )}
          </div>

          <button type="submit" className={buttonBase} disabled={submitting}>
            {submitting ? "VERIFYING..." : "SUBMIT"}
          </button>

          <div className="text-center text-[11px] text-slate-400">
            {flow?.resetEmail ? `OTP sent to: ${flow.resetEmail}` : ""}
          </div>
        </form>
      </div>
      <AuthPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </AuthLayout>
  );
}